import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toAssetUrl } from "../api/api";
import { deletePost, fetchPosts, parseTags, toggleStatus, toggleTrending } from "../api/postsApi";
import { fetchCategories } from "../api/categoriesApi";
import Loader from "./Loader";
import ConfirmModal from "./ConfirmModal";
import ToggleSwitch from "./ToggleSwitch";
import "../styles/PostList.css";

const PAGE_SIZE = 12;

export default function PostList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category") || "all";

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [trendingFilter, setTrendingFilter] = useState("all");
  const [page, setPage] = useState(1);

  function loadPosts() {
    setLoading(true);
    setError("");
    return fetchPosts()
      .then((data) => setItems(data))
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Failed to load posts.");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    let cancelled = false;

    loadPosts();
    fetchCategories()
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {
        /* category filter is optional — a failed fetch just leaves the list showing "All categories" */
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setCategoryFilter(value) {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("category");
    else next.set("category", value);
    setSearchParams(next);
  }

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (categoryFilter !== "all" && String(item.category_id) !== String(categoryFilter)) {
        return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!(item.title || "").toLowerCase().includes(q)) return false;
      }
      if (statusFilter !== "all") {
        const wantPublished = statusFilter === "published";
        if (Boolean(Number(item.status)) !== wantPublished) return false;
      }
      if (trendingFilter !== "all") {
        const wantTrending = trendingFilter === "trending";
        if (Boolean(Number(item.trending)) !== wantTrending) return false;
      }
      return true;
    });
  }, [items, categoryFilter, search, statusFilter, trendingFilter]);

  // Reset to page 1 whenever the filtered set changes shape.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, trendingFilter, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  async function handleToggleStatus(item) {
    const next = !Boolean(Number(item.status));
    setBusyId(item.id);
    try {
      await toggleStatus(item, next);
      setItems((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, status: next ? 1 : 0 } : p))
      );
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Could not update status.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggleTrending(item) {
    const next = !Boolean(Number(item.trending));
    setBusyId(item.id);
    try {
      await toggleTrending(item, next);
      setItems((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, trending: next ? 1 : 0 } : p))
      );
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Could not update trending status.");
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deletePost(deleteTarget.id);
      setItems((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Could not delete post.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="postlist">
      <div className="postlist-toolbar">
        <input
          className="postlist-search"
          placeholder="Search by title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="postlist-select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="postlist-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="unpublished">Unpublished</option>
        </select>

        <select
          className="postlist-select"
          value={trendingFilter}
          onChange={(e) => setTrendingFilter(e.target.value)}
        >
          <option value="all">All posts</option>
          <option value="trending">Trending only</option>
          <option value="not-trending">Not trending</option>
        </select>

        {/* <Link className="postlist-create-btn" to="/posts/create">
          + New Post
        </Link> */}
      </div>

      {loading && <Loader label="Loading posts..." />}
      {!loading && error && <p className="postlist-error">{error}</p>}

      {!loading && !error && filtered.length === 0 && (
        <p className="postlist-empty">No posts found for this filter.</p>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <div className="postlist-grid">
            {pageItems.map((item) => {
              const isBusy = busyId === item.id;
              const published = Boolean(Number(item.status));
              const trending = Boolean(Number(item.trending));
              const tags = parseTags(item.tags);

              return (
                <div className="postcard" key={item.id}>
                  <div className="postcard-image">
                    {item.imageUrl ? (
                      <img src={toAssetUrl(item.imageUrl)} alt="" />
                    ) : (
                      <div className="postcard-img-placeholder" />
                    )}
                    {trending && <span className="postcard-trend-flag">Trending</span>}
                  </div>

                  <div className="postcard-body">
                    {item.category?.name && (
                      <span className="postcard-category">{item.category.name}</span>
                    )}
                    <h3 className="postcard-title">{item.title}</h3>
                    {item.summary && <p className="postcard-summary">{item.summary}</p>}

                    {tags.length > 0 && (
                      <div className="postcard-tags">
                        {tags.slice(0, 3).map((t, i) => (
                          <span className="postcard-tag" key={i}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="postcard-badges">
                      <ToggleSwitch
                        label={published ? "Published" : "Unpublished"}
                        checked={published}
                        onChange={() => handleToggleStatus(item)}
                        disabled={isBusy}
                      />
                      <ToggleSwitch
                        label={trending ? "Trending" : "Normal"}
                        checked={trending}
                        onChange={() => handleToggleTrending(item)}
                        disabled={isBusy}
                        tone="trend"
                      />
                    </div>

                    {item.createdAt && (
                      <p className="postcard-date">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </p>
                    )}

                    <div className="postcard-actions">
                      <Link
                        className="postcard-btn postcard-btn-view"
                        to={`/posts/view/${item.id}`}
                        state={{ post: item }}
                      >
                        View
                      </Link>
                      <Link
                        className="postcard-btn postcard-btn-edit"
                        to={`/posts/edit/${item.id}`}
                        state={{ post: item }}
                      >
                        Edit
                      </Link>
                      <button
                        className="postcard-btn postcard-btn-delete"
                        onClick={() => setDeleteTarget(item)}
                        disabled={isBusy}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="postlist-pagination">
              <button
                className="postlist-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ← Prev
              </button>

              <span className="postlist-page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="postlist-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete this post?"
        message={deleteTarget ? `"${deleteTarget.title}" will be permanently removed.` : ""}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
